import { useCallback, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  TbExternalLink,
  TbEye,
  TbEyeOff,
  TbPlayerPause,
  TbPlayerPlay,
  TbTrash,
} from "react-icons/tb";
import { ShortcutCard } from "../../../../../Components/Atoms/ShortcutCard";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import { patchBaasAccountLifecycle } from "../../../../../Services/baasAccountsListApi";
import { CrossborderCopyButton } from "../../../Crossborder/components/CrossborderCopyButton";
import { STABLES_ACTION_ICONS } from "../../../Stables/stablesOperationIcons";
import {
  canShowBaasAccountLifecycleActions,
} from "../../baasCapabilities";
import {
  BAAS_STABLES_TRANSACTIONS_PATH,
  buildBaasStablesOperationPath,
} from "../../constants";
import {
  buildBaasStablesAddressExplorerUrl,
  truncateBaasStablesAddress,
} from "../baasStablesExplorer";

/** Same order/labels as IB StablesOperationsMenu (+ history for admin). */
const QUICK_ACTIONS = [
  {
    id: "deposit",
    labelKey: "baas.stablesWallet.quickActions.deposit",
    icon: STABLES_ACTION_ICONS.deposit,
    operation: "deposit",
  },
  {
    id: "buy",
    labelKey: "baas.stablesWallet.quickActions.buy",
    icon: STABLES_ACTION_ICONS.buy,
    operation: "buy",
  },
  {
    id: "sell",
    labelKey: "baas.stablesWallet.quickActions.sell",
    icon: STABLES_ACTION_ICONS.sell,
    operation: "sell",
  },
  {
    id: "swap",
    labelKey: "baas.stablesWallet.quickActions.swap",
    icon: STABLES_ACTION_ICONS.swap,
    operation: "swap",
  },
  {
    id: "withdraw",
    labelKey: "baas.stablesWallet.quickActions.withdraw",
    icon: STABLES_ACTION_ICONS.withdraw,
    operation: "withdraw",
  },
  {
    id: "history",
    labelKey: "baas.stablesWallet.quickActions.history",
    icon: STABLES_ACTION_ICONS.history,
    operation: null,
  },
];

export const BaasStablesSensitiveToggleButton = ({ visible, onToggle, className = "" }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`btn-primary shrink-0 !h-8 !min-h-8 !px-3 !text-xs ${className}`.trim()}
    aria-label={
      visible
        ? intl.get("baas.stablesWallet.quickActions.hideSensitiveAria")
        : intl.get("baas.stablesWallet.quickActions.showSensitiveAria")
    }
    aria-pressed={visible}
  >
    {visible ? <TbEyeOff className="h-3.5 w-3.5" aria-hidden /> : <TbEye className="h-3.5 w-3.5" aria-hidden />}
    {visible
      ? intl.get("baas.stablesWallet.quickActions.hideSensitive")
      : intl.get("baas.stablesWallet.quickActions.showSensitive")}
  </button>
);

const WalletHashBar = ({ address, explorerUrl, sensitiveVisible, onToggleSensitive }) => {
  if (!address) return null;

  const displayAddress = sensitiveVisible ? address : truncateBaasStablesAddress(address);
  const hasActions = typeof onToggleSensitive === "function" || Boolean(explorerUrl);

  return (
    <div className="mb-2 flex min-w-0 flex-col gap-2 rounded-xl border border-[#e7a73f]/35 bg-gradient-to-r from-brand-cream to-white px-3 py-2.5 dark:border-brand-gold-mid/30 dark:from-[#1a1a1a] dark:to-brand-dark-surface">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9a7340] dark:text-brand-gold-mid">
          {intl.get("baas.stablesWallet.quickActions.walletHash")}
        </p>
        <div className="mt-1 flex min-w-0 items-center gap-1.5">
          <code
            className="min-w-0 truncate font-mono text-xs font-medium text-brand-dark dark:text-gray-100"
            title={address}
          >
            {displayAddress}
          </code>
          <CrossborderCopyButton
            value={address}
            ariaLabel={intl.get("baas.stablesWallet.detail.copyAddress")}
          />
        </div>
      </div>

      {hasActions ? (
        <div className="flex flex-wrap items-center gap-2">
          {typeof onToggleSensitive === "function" ? (
            <BaasStablesSensitiveToggleButton
              visible={sensitiveVisible}
              onToggle={onToggleSensitive}
            />
          ) : null}
          {explorerUrl ? (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline shrink-0 !h-8 !min-h-8 !px-3 !text-xs no-underline"
              aria-label={intl.get("baas.stablesWallet.quickActions.scanExplorerAria")}
            >
              <TbExternalLink className="h-3.5 w-3.5" aria-hidden />
              {intl.get("baas.stablesWallet.quickActions.scanExplorer")}
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Wallet UI status → account lifecycle transitions.
 * Backend has no wallet-only lifecycle; PATCH …/accounts/{id}/lifecycle cascades
 * StableAccount (ACTIVE / SUSPENDED / CLOSED). Suspend maps to UI "blocked".
 */
const resolveWalletLifecycle = (walletStatus) => {
  const status = String(walletStatus || "active").toLowerCase();
  const isInactive = status === "inactive";
  const isBlocked = status === "blocked";
  return {
    canActivate: isBlocked || isInactive,
    canSuspend: !isInactive,
    canDeactivate: !isInactive,
    suspendDisabled: isBlocked,
  };
};

export const BaasStablesWalletQuickActions = ({
  disabled = false,
  walletId = null,
  address = null,
  network = null,
  sensitiveVisible = true,
  onToggleSensitive = null,
  /** Linked Conta Digital id — required for Ativar / Desativar / Suspender. */
  accountId = null,
  walletStatus = null,
  onLifecycleChanged = null,
}) => {
  const explorerUrl = buildBaasStablesAddressExplorerUrl(network, address);
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const { can, capabilities } = useBaasCapabilities();
  const canCreateAccount = can("accounts.create");
  const canLifecycle = Boolean(
    accountId
      && canShowBaasAccountLifecycleActions({
        embedded: false,
        capabilities,
        canCreateAccount,
      }),
  );
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const { canActivate, canSuspend, canDeactivate, suspendDisabled } = resolveWalletLifecycle(
    walletStatus,
  );

  const handleLifecycle = useCallback(
    async (action) => {
      if (!selectedInstance || !accountId || lifecycleBusy) return;

      const confirmKey =
        action === "activate"
          ? "baas.stablesWallet.lifecycle.activateConfirm"
          : action === "suspend"
            ? "baas.stablesWallet.lifecycle.suspendConfirm"
            : "baas.stablesWallet.lifecycle.deactivateConfirm";
      if (!window.confirm(intl.get(confirmKey))) return;

      setLifecycleBusy(true);
      try {
        try {
          await patchBaasAccountLifecycle(selectedInstance, accountId, {
            action,
            retainBalance: false,
          });
        } catch (err) {
          if (action === "deactivate" && err?.code === "balance_destination_required") {
            const retain = window.confirm(
              intl.get("baas.stablesWallet.lifecycle.retainBalance"),
            );
            if (!retain) return;
            await patchBaasAccountLifecycle(selectedInstance, accountId, {
              action,
              retainBalance: true,
            });
          } else {
            throw err;
          }
        }
        addToast({
          type: "success",
          title: intl.get("baas.stablesWallet.lifecycle.success"),
        });
        onLifecycleChanged?.();
      } catch (error) {
        addToast({
          type: "error",
          title: intl.get("baas.stablesWallet.lifecycle.failed"),
          description: formatBaasConsoleConflictMessage(
            error,
            error?.detail || error?.message || null,
          ),
        });
      } finally {
        setLifecycleBusy(false);
      }
    },
    [accountId, addToast, lifecycleBusy, onLifecycleChanged, selectedInstance],
  );

  const lifecycleActions = useMemo(() => {
    if (!canLifecycle) return [];

    const next = [];

    if (canSuspend) {
      next.push({
        id: "suspend",
        labelKey: "baas.accounts.lifecycle.suspend",
        icon: TbPlayerPause,
        disabled: lifecycleBusy || suspendDisabled,
        title: intl.get("baas.stablesWallet.lifecycle.suspendHint"),
        onClick: () => {
          void handleLifecycle("suspend");
        },
      });
    }

    if (canDeactivate) {
      next.push({
        id: "deactivate",
        labelKey: "baas.accounts.lifecycle.deactivate",
        icon: TbTrash,
        disabled: lifecycleBusy,
        title: intl.get("baas.stablesWallet.lifecycle.deactivateHint"),
        onClick: () => {
          void handleLifecycle("deactivate");
        },
      });
    }

    if (canActivate) {
      next.push({
        id: "activate",
        labelKey: "baas.accounts.lifecycle.activate",
        icon: TbPlayerPlay,
        disabled: lifecycleBusy,
        title: intl.get("baas.stablesWallet.lifecycle.activateHint"),
        onClick: () => {
          void handleLifecycle("activate");
        },
      });
    }

    return next;
  }, [
    canActivate,
    canDeactivate,
    canLifecycle,
    canSuspend,
    handleLifecycle,
    lifecycleBusy,
    suspendDisabled,
  ]);

  return (
    <section className="baas-stables-wallet-quick-actions min-w-0 w-full">
      <p className="dashboard-section-label mb-2">
        {intl.get("baas.stablesWallet.quickActions.section")}
      </p>

      <WalletHashBar
        address={address}
        explorerUrl={explorerUrl}
        sensitiveVisible={sensitiveVisible}
        onToggleSensitive={onToggleSensitive}
      />

      <div className="shortcut-scroll-row !gap-2 !pb-1">
        {QUICK_ACTIONS.filter((action) => !disabled || !action.operation).map(
          ({ id, labelKey, icon, operation }) => (
            <ShortcutCard
              key={id}
              icon={icon}
              label={intl.get(labelKey)}
              to={
                operation
                  ? buildBaasStablesOperationPath(operation, walletId)
                  : BAAS_STABLES_TRANSACTIONS_PATH
              }
            />
          ),
        )}
        {lifecycleActions.map(({ id, labelKey, icon, onClick, disabled: actionDisabled, title }) => (
          <ShortcutCard
            key={id}
            icon={icon}
            label={intl.get(labelKey)}
            onClick={onClick}
            disabled={actionDisabled === true}
            title={title}
          />
        ))}
      </div>
      {disabled ? (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.stablesWallet.quickActions.disabledHint")}
        </p>
      ) : null}
    </section>
  );
};
